import { orgLocalDate } from "@njbsoft/utils";

/** Filters out Go zero dates (0001-01-01) and falsy values. */
export function isValidDate(val: unknown): val is string {
  if (!val || typeof val !== "string") return false;
  return !val.startsWith("0001-01-01");
}

/**
 * Format a date-only string (YYYY-MM-DD) as a local date string.
 * Avoids the timezone-shift bug where `new Date("2026-04-15")` parses as
 * UTC midnight and displays as the previous day in negative-offset timezones.
 * Optional Intl options control the rendering (e.g. `{ month: "short" }`).
 */
export function formatDateOnly(dateStr: string, options?: Intl.DateTimeFormatOptions): string {
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, options);
}

/**
 * Returns the best available date for a sampling event.
 * Schedule-generated events set occurrence_date; manually-created events set
 * scheduled_date. Falls back through the chain, skipping Go zero dates.
 */
export function effectiveEventDate(event: {
  occurrence_date?: string | null;
  scheduled_date?: string | null;
  collected_date?: string | null;
  created_at?: string;
}): string | null {
  if (isValidDate(event.occurrence_date)) return event.occurrence_date;
  if (isValidDate(event.scheduled_date)) return event.scheduled_date;
  if (isValidDate(event.collected_date)) return event.collected_date;
  if (event.created_at) return event.created_at.split("T")[0] ?? null;
  return null;
}

/** Formats the effective event date for display, returning "-" if no valid date. */
export function formatEventDate(event: {
  occurrence_date?: string | null;
  scheduled_date?: string | null;
  collected_date?: string | null;
  created_at?: string;
}): string {
  const d = effectiveEventDate(event);
  return d ? formatDateOnly(d) : "-";
}

/** Checks if a date string represents a date before today (date-only, no time component). */
export function isExpiredDate(val: string): boolean {
  const date = new Date(val);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

/**
 * Format an ISO 8601 timestamp (e.g. `2026-06-26T03:49:35.557623Z`) as a clean
 * short local date (e.g. `Jun 24, 2026`). Reduces the timestamp to its
 * date-only portion and reuses `formatDateOnly` so the timezone-shift guard and
 * Intl rendering stay consistent with every other date surface. Falls back to
 * the raw string when empty, a Go zero time, or otherwise unparseable.
 *
 * Used by the document repository surfaces where the API returns a full
 * `created_at` timestamp but the UI wants a Drive-style short date.
 */
export function formatTimestampDate(
  val: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" },
): string {
  if (!val) return "-";
  if (!isValidDate(val)) return "-";
  const dateOnly = val.split("T")[0];
  if (!dateOnly) return val;
  return formatDateOnly(dateOnly, options);
}

/**
 * Format an ISO 8601 timestamp (e.g. `2026-04-22T14:05:12Z`) as a localized
 * date + time string in the viewer's timezone. Falls back to the raw string
 * when the value is empty, a Go zero time, or otherwise unparseable.
 *
 * Used by custody timeline rows to display `occurred_at` (the physical
 * event time) alongside human-readable actor / role metadata.
 */
export function formatDateTime(val: string | null | undefined): string {
  if (!val) return "-";
  if (!isValidDate(val)) return "-";
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleString();
}

const SHORT_DATE: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };

/**
 * An ISO 8601 instant as a short date and time in the viewer's zone
 * ("Jul 1, 2026, 03:04 PM") — the submission record's clock rendering.
 */
export function formatShortDateTime(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    ...SHORT_DATE,
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * A submission's performed value as the record prints it. `performedDate`
 * (`performed_date`, `YYYY-MM-DD`) is the calendar day the performer named
 * when the answer was a date: it is the day, printed as a date with no time
 * and never read through a zone. Null — or a row that carries no day — means
 * the answer was an instant, printed from `iso` with its time.
 */
export function formatPerformedAt(iso: string, performedDate: string | null): string {
  return performedDate ? formatDateOnly(performedDate, SHORT_DATE) : formatShortDateTime(iso);
}

/**
 * Converts an ISO 8601 instant to the value shape the native
 * `datetime-local` input accepts (`YYYY-MM-DDTHH:mm`, wall-clock in the
 * browser's local timezone, no seconds/offset). Returns "" for null/invalid
 * input so the input renders empty rather than throwing.
 */
export function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The viewer's local calendar day of an ISO 8601 instant, as the `YYYY-MM-DD`
 * value a native `date` input takes (and a date-only wire member carries).
 * Returns "" for null/invalid input, like `toDatetimeLocalValue`.
 */
export function toDateInputValue(iso: string | null | undefined): string {
  return toDatetimeLocalValue(iso).slice(0, 10);
}

/** Today in the viewer's zone, as a `date` input value (`YYYY-MM-DD`). */
export function todayDateInputValue(): string {
  return toDateInputValue(new Date().toISOString());
}

/**
 * An ISO 8601 instant as the viewer's local calendar day ("Aug 5, 2026").
 * Unlike `formatTimestampDate`, which keeps the instant's UTC date, this is the
 * day the instant fell on where the viewer is.
 */
export function formatInstantDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * An ISO 8601 instant as the calendar day the org's clock reads
 * ("Aug 5, 2026") — the day an org-scoped record was entered, the same in
 * every viewer's zone. "-" for a missing, Go-zero, or unparseable instant, and
 * while the org's zone is still loading.
 */
export function formatOrgLocalDay(
  value: string | null | undefined,
  timezone: string | undefined,
): string {
  if (!value || !timezone || !isValidDate(value)) return "-";
  if (Number.isNaN(new Date(value).getTime())) return "-";
  return formatDateOnly(orgLocalDate(value, timezone), SHORT_DATE);
}

/**
 * Converts a `datetime-local` input value back to an ISO 8601 instant for
 * the wire. Empty input maps to null (the clear affordance).
 */
export function fromDatetimeLocalValue(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
